export interface ConsentPreferences {
  statistics: boolean;
  marketing: boolean;
  timestamp: number;
}

const STORAGE_KEY = 'SFT_consent_preferences';
const LEGACY_STORAGE_KEY = 'SFT_cookie_warning_expire';

const CONSENT_EVENT = 'sft-consent-updated';

/**
 * Get the current consent preferences from localStorage.
 * Returns null if no consent has been recorded yet.
 */
export function getConsent(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as ConsentPreferences;
    }
  } catch {
    // localStorage unavailable or corrupted data
  }
  return null;
}

/**
 * Returns true if the user has made a consent choice (accept or reject).
 */
export function hasConsented(): boolean {
  return getConsent() !== null;
}

/**
 * Save consent preferences and dispatch global event.
 */
export function setConsent(prefs: Omit<ConsentPreferences, 'timestamp'>): void {
  const preferences: ConsentPreferences = {
    ...prefs,
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    // Remove legacy key if present
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: preferences }));
}

/**
 * Revoke all consent, delete tracking cookies, and dispatch event.
 */
export function revokeConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
  deleteTrackingCookies();
  window.dispatchEvent(
    new CustomEvent(CONSENT_EVENT, {
      detail: { statistics: false, marketing: false, timestamp: Date.now() },
    }),
  );
}

/**
 * Listen for consent changes.
 */
export function onConsentChange(callback: (prefs: ConsentPreferences) => void): () => void {
  const handler = (e: Event) => {
    callback((e as CustomEvent<ConsentPreferences>).detail);
  };
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}

export { CONSENT_EVENT };

/**
 * Delete known tracking cookies set by GA, GTM, and Facebook Pixel.
 */
function deleteTrackingCookies(): void {
  const cookiesToDelete = ['_ga', '_gid', '_gat', '_fbp', '_fbc'];
  const domain = window.location.hostname;
  // Also try parent domain (e.g. .example.com)
  const parts = domain.split('.');
  const parentDomain = parts.length > 2 ? '.' + parts.slice(-2).join('.') : '.' + domain;

  for (const name of cookiesToDelete) {
    // Delete with various path/domain combos
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain};`;
  }

  // Also delete _ga_* cookies (GA4 uses _ga_CONTAINERID)
  const allCookies = document.cookie.split(';');
  for (const cookie of allCookies) {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga_')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain};`;
    }
  }
}
